import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Company, Image } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { SettingsService } from 'src/app/shared/services/settings.service';


@Component({
  selector: 'app-company-crud',
  templateUrl: './company-crud.component.html',
  styleUrls: ['./company-crud.component.scss'],
})
export class CompanyCrudComponent implements OnInit {
  @Input() company: Company;
  @Input() companies: Company[] = [];
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  public onLoad: boolean = false;
  public companyForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  public periodTypes: string[] = [];
  private imageUrl: string;
  public image: string;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private settingsService: SettingsService,
    private authService: AuthService,
    private commonService: CommonService,
    private dataService: DataService,
    private domainService: DomainService,
  ) { }

  ngOnInit(): void {
    this.periodTypes = this.settingsService.getPeriodTypes();
    this.setCompany(this.company);
  }

  private setCompany(company?: Company) {
    const context: Context = this.authService.getContextData();
    if (!company) company = {
      id: null,
      name: '',
      website: '',
      contact: '',
      domain: '',
      gstIn: null,
      weblink: '',
      logo: '',
      banner: '',
      code: null,
      currencyId: context?.currencyId,
      timezoneId: context?.timezoneId,
      disabled: false,
      punchable: false,
      period: 'FISCAL_YEAR'
    } as Company;
    this.companyForm = this.fb.group({
      id: this.fb.control(company.id, []),
      name: this.fb.control(company.name, [Validators.required, Validators.pattern(RegExp.description)]),
      website: this.fb.control(company.website, [Validators.required, Validators.pattern(RegExp.website)]),
      contact: this.fb.control(company.contact, [Validators.required, Validators.pattern(RegExp.text)]),
      weblink: this.fb.control(company.weblink, []),
      domain: this.fb.control(company.domain, Validators.pattern(RegExp.domain)),
      gstIn: this.fb.control(company.gstIn, [Validators.pattern(RegExp.gst)]),
      logo: this.fb.control(company.logo, []),
      banner: this.fb.control(company.banner, []),
      code: this.fb.control(company.code, [Validators.required, Validators.pattern(RegExp.code)]),
      currencyId: this.fb.control(company.currencyId, [Validators.required]),
      timezoneId: this.fb.control(company.timezoneId, [Validators.required]),
      disabled: this.fb.control(company.disabled, []),
      punchable: this.fb.control(company.punchable, []),
      period: this.fb.control(company.period, [Validators.required, Validators.pattern(RegExp.text)]),
    });
    this.companyForm.valueChanges.subscribe(value => this.modified = true);
  }

  get f() {
    return this.companyForm.controls;
  }

  public async getAndSetBase64(event: any) {
    const image: string = await this.commonService.convertImgToBase64(event);
    if (!image) return;
    this.imageUrl = image;
  }


  private saveImage(sid: number, refType: string, label: string, url: string) {
    let image: Image = {
      id: null,
      sid: sid,
      type: refType,
      label: label,
      image: url,
    };
    image.image = url
    this.domainService.createImage(image).subscribe({
      next: (response: string) => {
      },
      error: (error: HttpErrorResponse) => {
      }
    });
  }

  private getImage() {
    this.domainService.getImage('COMPANY', 'COMPANY', this.company.id, 'logo').subscribe(image => {
      // this.image = image;
      this.image=image.payload;
    });
  }


  public saveCompany(inProgress?: any) {
    this.submitted = true;
    if (this.companyForm.valid) {
      let company: Company = this.companyForm.value;
      company.code = company.code.toUpperCase();
      if (this.companies.some(c => c.name.toLowerCase() === company.name.toLowerCase())) {
        this.commonService.setToastr(400, `Company ${company.name} already exists!`);
        return;
      }
      if (!this.modified) {
        this.commonService.setToastr(200, `Company ${company.name} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (company.id) {
        this.settingsService.updateCompany(company.id, company).subscribe({
          next: (response: Company) => {
            if (response) {
              this.passEntry.emit(response);
              inProgress.value = false;
              if(this.imageUrl){
                this.saveImage(company.id, 'COMPANY', 'logo', this.imageUrl);
              }
              this.commonService.setToastr(200, `Company ${response.name} has been updated successfully!`);
              this.dataService.reInitContext();
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.settingsService.createCompany(company).subscribe({
          next: (response: Company) => {
            if (response) {
              this.passEntry.emit(response);
              this.onLoad = true;
              if(this.imageUrl){
                this.saveImage(company.id, 'COMPANY', 'logo', this.imageUrl);
              }
              inProgress.value = false;
              this.commonService.setToastr(200, `Company ${response.name} has been created successfully!`);
              this.setCompany(response);
              this.dataService.reInitContext();
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }


}
